import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useContext,
  useEffect,
  useRef,
} from "react";

import { isEqual } from "lodash";

import type {
  CommandResultSource,
  CommandScope,
} from "src/components/commandbar/types";

type CommandResultsContextType = {
  search: string;
  scope: CommandScope | null;
  setSources: Dispatch<SetStateAction<Map<string, CommandResultSource>>>;
};

export const CommandResultsContext = createContext<CommandResultsContextType>({
  search: "",
  scope: null,
  setSources: () => {},
});

// What the open palette is searching for. Sources use it to decide what to fetch.
export const useCommandBarQuery = () => {
  const { search, scope } = useContext(CommandResultsContext);
  return { search, scope };
};

// Sources mount inside the open palette (see Palette), so their queries run only while it is open.
// Unchanged results are ignored, so callers do not need to memoize them or `onRetry`.
export const useRegisterCommandResults = ({
  onRetry,
  ...rest
}: CommandResultSource) => {
  const { setSources } = useContext(CommandResultsContext);
  const latestRetry = useRef(onRetry);
  latestRetry.current = onRetry;
  // A fresh function every render would never compare equal and would register again forever.
  const stableRetry = useRef(() => latestRetry.current?.()).current;
  const source: CommandResultSource = {
    ...rest,
    ...(onRetry && { onRetry: stableRetry }),
  };

  useEffect(() => {
    setSources((current) => {
      if (isEqual(current.get(source.id), source)) {
        return current;
      }
      return new Map(current).set(source.id, source);
    });
  });

  useEffect(
    () => () =>
      setSources((current) => {
        const next = new Map(current);
        next.delete(source.id);
        return next;
      }),
    [source.id, setSources],
  );
};
